import { useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PageContext } from 'src/context/PageContext';
import { PREFERENCES_QUERY_KEY } from 'src/components/Preferences/usePreferences';
import { subscribeToNotifications } from 'src/service/engageFrontendGateway';

// Shared write path for both the anonymous email form and the authenticated
// opt-in checkbox. Resolves the page/survey target the resident is subscribing
// from, then hits the same sign-up endpoint with the configured publication ids.
export function useNotificationSignupSubscribe(residentCustomPublicationIds?: string[]) {
  const { page, urlParams, survey, project, submissionUuid } = useContext(PageContext);
  const { locale, organization } = urlParams || {};
  const queryClient = useQueryClient();

  const projectTitle =
    typeof project?.title === 'string'
      ? project?.title
      : project?.title?.[urlParams?.locale || ''] || project?.title?.[Object.keys(project?.title || {})[0]];
  const titleLocale = urlParams?.locale || survey?.default_language || survey?.fallback_language || '';
  const surveyTitle =
    survey?.title_for_display?.[titleLocale] ||
    survey?.title_for_display?.[survey.default_language!] ||
    survey?.title_for_display?.[survey.fallback_language!];
  const surveyProjectObject =
    !!survey && !!project
      ? {
          id: survey.structure.id,
          page_type: page?.page_type ?? 'activity',
          customer_id: survey.client?.id,
          name: surveyTitle || projectTitle || project?.slug,
        }
      : null;

  const subscribe = async (email: string): Promise<void> => {
    // Preview mode collects no data: short-circuit the sign-up so the UI can show
    // its success state without any outgoing network call.
    if (urlParams?.preview) {
      return;
    }
    if (page && organization && locale) {
      await subscribeToNotifications(
        email,
        page,
        organization as string,
        locale as string,
        residentCustomPublicationIds,
        submissionUuid,
      );
    } else if (organization && locale && survey) {
      await subscribeToNotifications(
        email,
        (surveyProjectObject ?? {
          customer_id: survey.client?.id,
          page_type: 'activity',
          id: survey.structure.id,
        }) as never,
        organization as string,
        locale as string,
        residentCustomPublicationIds,
        submissionUuid,
      );
    }
    // Refetch the authenticated resident's subscriptions so any rendered
    // opt-in row promotes to its "already subscribed" pre-checked state.
    // No-op for anonymous residents since their query is never cached.
    await queryClient.invalidateQueries({ queryKey: PREFERENCES_QUERY_KEY });
  };

  return { subscribe };
}
